import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, PLATFORM_ID, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';

import { HttpClient, provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { IPSAuthenticationService, IPSHttpErrorInterceptor, IPSStylesModule, IPSTenantKeyService, IPSxTenantKeyInterceptor, OpenReplayService, provideIPSAuth, provideObservability } from '@ips/common-angular';
import Aura from '@primeng/themes/aura';
import { OAuthService, provideOAuthClient } from 'angular-oauth2-oidc';
import { Configuration as CasefileConfiguration, CaseFileItemService, CaseFileService } from 'com.trumbull.phx.casefile.api-client';
import { Configuration as DocumentConfiguration, DocumentService } from 'com.trumbull.phx.document.api-client';
import { Configuration as OrderConfiguration, OrderService } from 'com.trumbull.phx.order.api-client';
import { Configuration as VocabularyConfiguration, VocabularyService } from 'com.trumbull.phx.vocabulary.api-client';
import { Configuration as ClaimConfiguration, ClaimItemService } from 'com.trumbull.phx.claim.api-client';
import { Configuration as WorkConfiguration, WorkService } from 'com.trumbull.phx.work.api-client';
import { Configuration as ContactListConfiguration, ContactListService  } from 'com.trumbull.phx.contactlist.api-client';
import { Configuration as FeatureConfiguration, FeatureService  } from 'com.trumbull.phx.feature.api-client';
import { provideToastr } from 'ngx-toastr';
import { providePrimeNG } from 'primeng/config';
import { routes } from './app.routes';

import { Configuration as RecordRetrievalConfiguration, RecordRetrievalItemService, RecordRetrievalService } from 'com.trumbull.phx.recordretrievalobjective.api-client';

import packageJson from '../../../client-portal-app/package.json';
import { environment } from '@env/environment';
import { GlobalErrorHandler } from './interceptors/global-error-handler';
import { AppConfigService } from './app-config.service';
import { initOpenReplay } from './openreplay/openreplay.init';
import { DatePipe } from '@angular/common';
import { Configuration as UserConfiguration, UserService } from 'com.trumbull.phx.user.api-client';
import { Configuration as CommentListConfiguration, CommentListService } from 'com.trumbull.phx.commentlist.api-client';

export const appConfig: ApplicationConfig = {
  providers: [
    IPSTenantKeyService,
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideClientHydration(withEventReplay()),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          prefix: 'p',
          darkModeSelector: false,
          cssLayer: false
        }
      }
    }),
    provideHttpClient(
        withFetch(),
        withInterceptors([IPSxTenantKeyInterceptor, IPSHttpErrorInterceptor])
    ),
    provideOAuthClient(),
    provideIPSAuth(),
    {
      provide: APP_INITIALIZER,
      useFactory: initOpenReplay,
      deps: [OpenReplayService, AppConfigService, IPSAuthenticationService, OAuthService, PLATFORM_ID],
      multi: true
    },
    provideToastr(),
    OrderConfiguration,
    CasefileConfiguration,
    DocumentConfiguration,
    VocabularyConfiguration,
    RecordRetrievalConfiguration,
    ClaimConfiguration,
    UserConfiguration,
    WorkConfiguration,
    CommentListConfiguration,
    ContactListConfiguration,
    FeatureConfiguration,
    {
      provide: OrderService,
      deps: [HttpClient, OrderConfiguration],
      useFactory: (httpClient: HttpClient, configuration: OrderConfiguration) => new OrderService(httpClient, '/api/order', configuration)
    },
    {
      provide: CaseFileService,
      deps: [HttpClient, CasefileConfiguration],
      useFactory: (httpClient: HttpClient, configuration: CasefileConfiguration) => new CaseFileService(httpClient, '/api/casefile', configuration)
    },
    {
      provide: CaseFileItemService,
      deps: [HttpClient, CasefileConfiguration],
      useFactory: (httpClient: HttpClient, configuration: CasefileConfiguration) => new CaseFileItemService(httpClient, '/api/case-file', configuration)
    },
    {
      provide: DocumentService,
      deps: [HttpClient, DocumentConfiguration],
      useFactory: (httpClient: HttpClient, configuration: DocumentConfiguration) => new DocumentService(httpClient, '/api/document', configuration)
    },
    {
      provide: VocabularyService,
      deps: [HttpClient, VocabularyConfiguration],
      useFactory: (httpClient: HttpClient, configuration: VocabularyConfiguration) => new VocabularyService(httpClient, '/api/vocab', configuration)
    },
    {
      provide: RecordRetrievalService,
      deps: [HttpClient, RecordRetrievalConfiguration],
      useFactory: (httpClient: HttpClient, configuration: RecordRetrievalConfiguration) => new RecordRetrievalService(httpClient, '/api/record-retrieval-objective', configuration)
    },
    {
      provide: RecordRetrievalItemService,
      deps: [HttpClient, RecordRetrievalConfiguration],
      useFactory: (httpClient: HttpClient, configuration: RecordRetrievalConfiguration) => new RecordRetrievalItemService(httpClient, '/api/record-retrieval-objective', configuration)
    },
    {
      provide: ClaimItemService,
      deps: [HttpClient, ClaimConfiguration],
      useFactory: (httpClient: HttpClient, configuration: ClaimConfiguration) => new ClaimItemService(httpClient, '/api/claim', configuration)
    },    
    {
      provide: UserService,
      deps: [HttpClient, UserConfiguration],
      useFactory: (httpClient: HttpClient, configuration: UserConfiguration) => new UserService(httpClient, '/api/user', configuration)
    },
    {
      provide: WorkService,
      deps: [HttpClient, WorkConfiguration],
      useFactory: (httpClient: HttpClient, configuration: WorkConfiguration) => new WorkService(httpClient, '/api/work', configuration)
    },
    {
      provide: CommentListService,
      deps: [HttpClient, CommentListConfiguration],
      useFactory: (httpClient: HttpClient, configuration: CommentListConfiguration) => new CommentListService(httpClient, '/api/comment-list', configuration)
    },
    {
      provide: ContactListService,
      deps: [HttpClient, ContactListConfiguration],
      useFactory: (httpClient: HttpClient, configuration: ContactListConfiguration) => new ContactListService(httpClient, '/api/contact-list', configuration)
    },
    {
      provide: FeatureService,
      deps: [HttpClient, FeatureConfiguration],
      useFactory: (httpClient: HttpClient, configuration: FeatureConfiguration) => new FeatureService(httpClient, '/api/feature', configuration)
    },

    environment.observabilityEnabled ? provideObservability({
      appName: packageJson.name,
      version: packageJson.version,
      withInstrumentation: true,
    }) : [],
    IPSStylesModule,
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler
    },
    DatePipe,
  ]
};
